import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { handleItemNotFoundRedirect } from 'src/utils/item-not-found-redirect';

import { getConfig } from 'src/global-config';

import { toast } from 'src/components/snackbar';

import { getAxiosToken, refreshHRToken  } from './axios-store';

/**
 * Axios Instance Configuration with Dynamic Token Handling
 * 
 * This axios instance uses a request interceptor to dynamically set the Authorization header
 * for each request, rather than setting it statically at initialization time.
 * 
 * The interceptor checks for tokens in the following order:
 * 1. customToken in localStorage (for custom authentication)
 * 2. jwt_access_token in sessionStorage (for JWT authentication)
 * 
 * This approach solves the issue where tokens were being read from storage before
 * they were set, resulting in empty Authorization headers.
 * 
 * The interceptor handles both JSON-stringified tokens (customToken) and plain string tokens (JWT).
 */

// ----------------------------------------------------------------------

const FORBIDDEN_TOAST_ID = 'hr-forbidden';

/** Plan-gating 403s carry their own error code and UI; only the role refusals get the generic toast. */
function notifyForbidden(error: any) {
  const data = error?.response?.data ?? {};
  const code = String(data?.error ?? data?.Title ?? '');
  if (code && code !== 'Forbidden' && code !== 'ForbiddenException') {
    return;
  }

  toast.error('No permission', {
    id: FORBIDDEN_TOAST_ID,
    description:
      typeof data?.Description === 'string' && data.Description
        ? data.Description
        : 'Your role does not allow this action. Ask an organization administrator if you need access.',
  });
}

export function getAxiosInstance() {
  const CONFIG = getConfig();

  const instance = axios.create({
    baseURL: CONFIG.develop.baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = getAxiosToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      else{
        console.warn('HR Auth Token not found');
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,

    async (error) => {
      console.log('error', error.response);
      const originalRequest = error.config;

      // Network error / no response
      if (!error.response) {
        return Promise.reject(error);
      }

      if (handleItemNotFoundRedirect(error)) {
        return new Promise(() => {});
      }

      // 403: the member's role does not allow this action (or the token predates the role claim). The
      // request still rejects so the caller's own error path runs; this toast is the one message every
      // screen shows for it, deduplicated so a page that fires several guarded calls does not stack them.
      if (error.response.status === 403) {
        notifyForbidden(error);
        return Promise.reject(error);
      }

      // Only handle 401
      if (error.response.status !== 401) {
        console.log('error.response.status', error.response.status);
        //here we have to check if status is 404 and 
        return Promise.reject(error);
      }

      // Prevent infinite retry loop
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        //refresh (single-flight)
        await refreshHRToken();

        //token from memory
        const newToken = getAxiosToken();

        if (!newToken) {
          // Refresh failed → logout already handled
          return Promise.reject(error);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );

  return instance;
}


// ----------------------------------------------------------------------

/** The message the API sent with a rejected request; empty when the failure carried none. */
export const apiErrorMessage = (error: unknown): string =>
  axios.isAxiosError<{ Description?: string }>(error)
    ? (error.response?.data?.Description ?? '')
    : '';

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const deleteFetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.delete(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to delete:', error);
    throw error;
  }
};

export const postFetcher = async ([url, body]: [string, Record<string, any> | string]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const res = await axiosInstance.post(url, body);
    return res.data;
  } catch (error) {
    console.error('Failed to fetch (POST):', error);
    throw error;
  }
};

// ----------------------------------------------------------------------
export const updateCall = async (url: string, data: any, config: AxiosRequestConfig, method: 'POST' | 'PUT' = 'PUT') => {
  try {
    const axiosInstance = getAxiosInstance();
    const res = method === 'POST' 
      ? await axiosInstance.post(url, data, { ...config })
      : await axiosInstance.put(url, data, { ...config });
    return res.data;
  } catch (error) {
    console.error(`Failed to ${method.toLowerCase()}:`, error);
    throw error;
  }
};

// ----------------------------------------------------------------------

/**
 * Helper function to get the authentication token from storage
 * Uses the same logic as the axios interceptor for consistency
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('customToken');
  if (token) {
    try {
      let parsedToken = token;
      if (token.startsWith('"') || token.startsWith('{')) {
        const parsed = JSON.parse(token);
        parsedToken = typeof parsed === 'string' ? parsed : token;
      }
      if (parsedToken && typeof parsedToken === 'string') {
        return parsedToken;
      }
    } catch (error) {
      console.warn('Failed to parse token from storage:', error);
    }
  }
  return null;
}

/**
 * Generic streaming function for Server-Sent Events or streaming responses
 * Automatically handles authentication using the same token logic as axios
 * 
 * @param endpoint - The API endpoint to call (e.g., endpoints.organizationRag.queryRagStoreStream.path from src/lib/api)
 * @param data - The data to send in the POST request body
 * @param onStreamChunk - Callback function to handle each chunk of streamed data
 * @param config - Optional additional fetch configuration
 */
// Server JSON-encodes each stream chunk so newlines/whitespace survive SSE framing intact.
// Decode back to the original text; fall back to the raw payload for any non-JSON-string value
// (e.g. an older backend that streamed plain text).
const emitStreamChunk = (payload: string, onStreamChunk: (chunk: string) => void) => {
  let text = payload;
  try {
    const parsed = JSON.parse(payload);
    if (typeof parsed === 'string') text = parsed;
  } catch {
    // keep raw payload
  }
  onStreamChunk(text);
};

/** A streamed request the server refused before streaming, with the Description its error body gave, if any. */
export class StreamRequestError extends Error {
  constructor(
    readonly status: number,
    readonly description: string
  ) {
    super(description || `HTTP error! status: ${status}`);
  }
}

const errorDescriptionOf = async (response: Response) => {
  try {
    const body = await response.json();
    return typeof body?.Description === 'string' ? body.Description : '';
  } catch {
    return '';
  }
};

export const streamFetcher = async (
  endpoint: string,
  data: any,
  onStreamChunk: (chunk: string) => void,
  config?: RequestInit
) => {
  const CONFIG = getConfig();
  const url = `${CONFIG.develop.baseUrl}${endpoint}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept": "text/event-stream",
  };

  if (config?.headers) {
    const configHeaders = new Headers(config.headers);
    configHeaders.forEach((value, key) => (headers[key] = value));
  }

  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers,
      ...config,
    });

    if (!response.ok) {
      throw new StreamRequestError(response.status, await errorDescriptionOf(response));
    }
    if (!response.body) {
      throw new Error("Readable stream not supported.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // FIX #2: Split by SSE event delimiter
      const events = buffer.split("\n\n");
      buffer = events.pop() || ""; // part of next event

      for (const event of events) {
        const line = event.trim();
        if (!line || !line.startsWith("data:")) continue;

        // Per SSE spec: strip only the single optional space after "data:"
        const raw = line.substring(5);
        const payload = raw.startsWith(' ') ? raw.substring(1) : raw;

        // FIX #3: Stop on [DONE]
        if (payload === "[DONE]") return;

        emitStreamChunk(payload, onStreamChunk);
      }
    }

    // Handle leftover buffer (unlikely but safe)
    const last = buffer.trim();
    if (last.startsWith("data:")) {
      const rawLast = last.substring(5);
      const payload = rawLast.startsWith(' ') ? rawLast.substring(1) : rawLast;
      if (payload !== "[DONE]") emitStreamChunk(payload, onStreamChunk);
    }
  } catch (error) {
    console.error("Streaming error:", error);
    throw error;
  }
};


// ----------------------------------------------------------------------

/** Routes still called without the typed client: the JWT template's auth routes, which the Web API does not serve, the
 * job create, whose body omits fields the contract requires, and the member, agent builder, job settings and coupon
 * calls, which the contract covers but their callers have yet to adopt. */
export const endpoints = {
  auth: {
    me: '/api/auth/me',
    signIn: '/api/auth/sign-in',
    signUp: '/api/auth/sign-up',
  },
  jobs: {
    create: '/api/JobPostings',
  },
  members: {
    remove: (userId: string) => `/api/Account/Users/${userId}`,
    changeRole: (userId: string) => `/api/Account/Users/${userId}/Role`,
  },
  interview_agents: {
    list: '/api/InterviewAgents',
    create: '/api/InterviewAgents',
    get: (agentId: string | number) => `/api/InterviewAgents/${agentId}`,
    update: (agentId: string | number) => `/api/InterviewAgents/${agentId}`,
    delete: (agentId: string | number) => `/api/InterviewAgents/${agentId}`,
    profilePicture: (agentId: string | number) => `/api/InterviewAgents/${agentId}/ProfilePicture`,
  },
  job_settings: {
    get: (jobId: string) => `/api/JobPostings/${jobId}/Settings`,
    setSetting: (jobId: string, key: string) => `/api/JobPostings/${jobId}/Settings/${encodeURIComponent(key)}`,
    createRule: (jobId: string) => `/api/JobPostings/${jobId}/AutomationRules`,
    updateRule: (jobId: string, ruleId: string | number) => `/api/JobPostings/${jobId}/AutomationRules/${ruleId}`,
    deleteRule: (jobId: string, ruleId: string | number) => `/api/JobPostings/${jobId}/AutomationRules/${ruleId}`,
  },
  billing: {
    couponPreview: '/api/billing/coupons/preview',
    couponRedeem: '/api/billing/coupons/redeem',
  },
};
